import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DocumentUploadModal = ({ isOpen, onClose, onUpload, propertyId, loading = false }) => {
  const [formData, setFormData] = useState({
    type: 'AUTRE',
    title: '',
    description: '',
    tags: '',
    file: null
  });
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');

  const documentTypes = [
    { value: 'INSPECTION', label: 'Inspection' },
    { value: 'NOTAIRE', label: 'Contrat notaire' },
    { value: 'CERTIFICAT_LOCALISATION', label: 'Certificat localisation' },
    { value: 'BAIL', label: 'Bail' },
    { value: 'FACTURE', label: 'Facture' },
    { value: 'GARANTIE', label: 'Garantie' },
    { value: 'PERMIS', label: 'Permis' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setError('Type de fichier non supporté. Formats acceptés: PDF, JPEG, PNG, DOC, DOCX');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Le fichier est trop volumineux. Taille maximale: 10MB');
      return;
    }

    setFormData(prev => ({
      ...prev,
      file,
      title: prev.title || file.name.replace(/\.[^/.]+$/, '')
    }));
    setError('');
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    if (!formData.title.trim()) {
      setError('Le titre est requis');
      return;
    }

    const tagsArray = formData.tags
      ? formData.tags.split(',').map(t => t.trim()).filter(t => t.length > 0)
      : [];

    await onUpload({
      file: formData.file,
      type: formData.type,
      title: formData.title.trim(),
      description: formData.description.trim() || null,
      tags: tagsArray
    });

    // Reset form
    setFormData({
      type: 'AUTRE',
      title: '',
      description: '',
      tags: '',
      file: null
    });
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--spacing-md)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-card)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-lg)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-lg)' }}>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'var(--color-foreground)',
            margin: 0
          }}>
            Ajouter un document
          </h2>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <Select
              label="Type de document"
              value={formData.type}
              onChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
              options={documentTypes}
              required
            />

            <Input
              label="Titre"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex: Inspection pré-achat 2024"
              required
            />

            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Fichier
              </label>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${dragActive ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-xl)',
                  textAlign: 'center',
                  background: dragActive ? 'var(--color-primary)/5' : 'var(--color-muted)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)'
                }}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
                {formData.file ? (
                  <div>
                    <Icon name="File" size={32} className="text-primary mb-2" />
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                      color: 'var(--color-foreground)',
                      margin: '8px 0 0 0'
                    }}>
                      {formData.file.name}
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      margin: '4px 0 0 0'
                    }}>
                      {(formData.file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, file: null }));
                      }}
                      style={{ marginTop: 'var(--spacing-xs)' }}
                    >
                      Changer de fichier
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Icon name="Upload" size={32} className="text-muted-foreground mb-2" />
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                      color: 'var(--color-foreground)',
                      margin: '8px 0 0 0'
                    }}>
                      Glissez-déposez un fichier ici
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      margin: '4px 0 0 0'
                    }}>
                      ou cliquez pour sélectionner
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      margin: '8px 0 0 0'
                    }}>
                      PDF, JPEG, PNG, DOC, DOCX (max 10MB)
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Description (optionnel)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ajoutez une description..."
                rows={3}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                  color: 'var(--color-foreground)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>

            <Input
              label="Tags (séparés par des virgules)"
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="Ex: important, 2024, rénovation"
            />

            {error && (
              <div style={{
                padding: 'var(--spacing-sm)',
                background: 'var(--color-destructive)/10',
                border: '1px solid var(--color-destructive)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-destructive)',
                fontSize: '0.875rem',
                fontFamily: 'var(--font-body)'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end', marginTop: 'var(--spacing-md)' }}>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName="Upload"
                loading={loading}
                disabled={!formData.file || !formData.title.trim()}
              >
                Ajouter
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
